/**
 * `helper/adhocTableHelpers` — apps/web only; mobile has no such file (adhoc tables are a
 * desktop-POS feature). Uplifted WHOLE, because `utils/apiService`'s response interceptor calls
 * `getUpdatedAdhocTableData`, and that pulls in every other function here:
 * `getUpdatedAdhocTableData -> updateAdhocTables -> {getAdhocTables -> filterStaleTables,
 * removeAdhocTable -> filterAdhocTableOrders, getUpdatedAdhocOrders}`. There is no smaller
 * closure to take, so the whole 131-line file moves and the app file becomes a re-export shim
 * for its 13 importers.
 *
 * The two `store.dispatch` calls go through the STORE BRIDGE; both slices they dispatch into
 * (`MyOrderSlice`, `TableViewSlice`) are already in core, deep-imported at their `.desktop`
 * variants per hazard 16.
 *
 * Bodies are verbatim from the app, including the two latent bugs below — this migration
 * changes no behaviour, and both belong on a cleanup ticket rather than here:
 *   - `getUpdatedAdhocOrders` early-returns `[saleData]` when the table ALREADY has orders
 *     (`if (!!currentTableData?.orders?.length) return [saleData];`), discarding them; the
 *     merge logic below it only ever runs for an empty/absent table, where it then spreads
 *     `currentTableData?.orders` and would throw on `undefined`.
 *   - the `findIndex` callback on line ~"orderIndex" has no `return`, so it always yields -1.
 */
import moment from "moment";

import { isAdhocSale, getAdhocTableIndex, ADHOC_TABLES_STORAGE_KEY } from "./shared";

import { fetchOrderData } from "../../redux-store/slices/MyOrderSlice";
import { getTableOrdersDataSuccess } from "../../redux-store/slices/TableViewSlice";
import { getLocalStorageWithParse, setLocalStorage } from "../../utils/localStorage";
import { LOCALSTORAGE_KEYS, SALE_STATUS_TYPES } from "../../utils/constants";
import store from "../../redux-store";

export { ADHOC_TABLES_STORAGE_KEY };

export const ADHOC_TABLE_CLEAR_TIME = 10;

export function updateAdhocTables(saleData: any) {
  if (!isAdhocSale(saleData)) return;

  // check if this order is closed or cancelled
  const saleStatuses = [SALE_STATUS_TYPES.CLOSED, SALE_STATUS_TYPES.CANCELLED];
  if (saleStatuses.includes(saleData?.saleStatus)) {
    removeAdhocTable(saleData);
    return;
  }
  let adhocTables = getAdhocTables(true);

  let tableIndex = getAdhocTableIndex(adhocTables, saleData);

  let currentTableData = adhocTables[tableIndex];
  let newOrders = getUpdatedAdhocOrders(currentTableData, saleData);
  adhocTables[tableIndex] = {
    ...currentTableData,
    currentStatus: "Occupied",
    orders: newOrders,
    lastUpdated: moment(),
  };
  store.dispatch(getTableOrdersDataSuccess(newOrders));
  setLocalStorage(LOCALSTORAGE_KEYS.adhocTables, JSON.stringify(adhocTables));
}

/**
 *
 * @param currentTableData
 * @param saleData
 * @returns updated orders for the adhoc table using saleData
 */
export function getUpdatedAdhocOrders(currentTableData: any, saleData: any) {
  if (!!currentTableData?.orders?.length) return [saleData];
  let newOrders = [...currentTableData?.orders];
  let orderIndex = newOrders.findIndex((order: any) => {
    saleData?.mid === order?.mid;
  });
  if (orderIndex === -1) newOrders = [...newOrders, saleData];
  else newOrders[orderIndex] = { ...saleData };
  return newOrders;
}

/**
 *
 * @param tableData
 * @param saleData
 * @returns updated table data with current saleData removed
 */
function filterAdhocTableOrders(tableData: any, saleData: any) {
  let updatedTable = { ...tableData },
    newOrders =
      updatedTable?.orders?.filter(
        (order: any) => order?.mid !== saleData?.mid
      ) || [];
  updatedTable.orders = newOrders;
  return updatedTable;
}

/**
 *
 * @param saleData
 * remove or update adhoc table on which this order was placed
 */
export function removeAdhocTable(saleData: any) {
  if (!isAdhocSale(saleData)) return;
  let adhocTables = getAdhocTables();
  let tableIndex = getAdhocTableIndex(adhocTables, saleData);
  if (tableIndex === -1) return;
//check if this was the only order on this table
  if (adhocTables[tableIndex]?.orders?.length === 1) adhocTables.splice(tableIndex, 1);
  else {
    adhocTables[tableIndex] = filterAdhocTableOrders(adhocTables[tableIndex], saleData);
  }
  setLocalStorage(LOCALSTORAGE_KEYS.adhocTables, JSON.stringify(adhocTables));
}

/**
 *
 * @param tables
 * @returns filters free tables with no activity for more than ADHOC_TABLE_CLEAR_TIME
 */
function filterStaleTables(tables: any[]) {
  let filteredTables = [...tables];
  let currentTime = moment();
  filteredTables = filteredTables.filter((table: any) => {
    if (table?.currentStatus === "Occupied") return true;
    let duration = moment.duration(currentTime.diff(table?.lastUpdated));
    return duration.asMinutes() < ADHOC_TABLE_CLEAR_TIME;
  });
  setLocalStorage(
    LOCALSTORAGE_KEYS.adhocTables,
    JSON.stringify(filteredTables)
  );
  return filteredTables;
}

export function getAdhocTables(isUpdate = false) {
  let tables = getLocalStorageWithParse(LOCALSTORAGE_KEYS.adhocTables) || [];
  if (isUpdate) return tables;
  return filterStaleTables(tables);
}

/**
 *
 * @param saleData
 * update local adhoc tables data if updated by other user
 */
export function getUpdatedAdhocTableData(saleData: any) {
  if (!saleData?.serviceLabel?.isAdhoc) return;
  let payload = {
    saleId: saleData?.mid,
    callback: function (response: any = {}) {
      if (!response?.code) updateAdhocTables(response);
    },
  };
  store.dispatch(fetchOrderData(payload));
}

export * from "./shared";
